import amplitude, { AmplitudeClient } from "amplitude-js";
import Cookies from "js-cookie";

import logger from "@/utils/logger";

import { AuthUserData } from "@/types/auth";
import { Locale } from "@/types/locale";

import { localeCountryCodeMap } from "../translations/config";
import { UI_CONTEXT, WEBSITE_VERSION_V2 } from "./constants";
import EVENTS from "./events";

const COOKIE_DURATION_MINUTES = 43200;
const isBrowser = () => typeof window !== "undefined";

let _amplitudeInstance: amplitude.AmplitudeClient;
let _inAppUserId: string | undefined;

/*
 * The mobile app injects the consumer UUID into its gift card checkout webview. The app and
 * checkout already use that UUID as the Amplitude user id, so setting it here joins the
 * webview's events to the same user. Outside the webview the value is absent and web identity
 * is unchanged. Identity is best-effort: a failure must never stop the event being logged.
 */
const syncInAppUserId = (amplitudeInstance: AmplitudeClient) => {
    try {
        const consumerUuid = window.apAppConsumerUuid;
        if (!consumerUuid || consumerUuid === _inAppUserId) return;

        amplitudeInstance.setUserId(consumerUuid);
        _inAppUserId = consumerUuid;
    } catch (e) {
        logger.logException(e, "failed to set in-app user id");
    }
};

const getAmplitudeInstance = (): amplitude.AmplitudeClient | undefined => {
    if (!isBrowser()) {
        return;
    }

    if (!_amplitudeInstance) {
        _amplitudeInstance = amplitude.getInstance();
        _amplitudeInstance.init(process.env.NEXT_PUBLIC_AMPLITUDE_CLIENT_ID, null, {
            includeUtm: true,
            includeReferrer: true,
            unsetParamsReferrerOnNewSession: true,
            includeGclid: true,
        });

        setDeviceIdCookie(_amplitudeInstance);
    }

    syncInAppUserId(_amplitudeInstance);
    return _amplitudeInstance;
};

export const trackPageLoad = (
    path: string,
    locale: string,
    eventName?: string,
    eventProps?: Record<string, unknown>
): void => {
    try {
        const amplitudeInstance = getAmplitudeInstance();
        if (!amplitudeInstance) return;

        amplitudeInstance.logEvent(eventName || EVENTS.SHOP_DIRECTORY_PAGE_LOADING, {
            pageLoaded: true,
            websiteVersion: WEBSITE_VERSION_V2,
            path,
            country: localeCountryCodeMap[locale],
            locale,
            ...eventProps,
        });
    } catch (e) {
        logger.logException(e, "failed to track page load");
    }
};

const setDeviceIdCookie = (amplitudeInstance: AmplitudeClient) => {
    const { deviceId } = amplitudeInstance.options;
    Cookies.set("deviceId", deviceId, {
        domain: window.location.host,
        expires: COOKIE_DURATION_MINUTES,
    });
};

export const trackUserEvent = (
    locale: string,
    path: string,
    uiContext: UI_CONTEXT,
    eventName = EVENTS.BUTTON_CLICK,
    eventProps?: Record<string, unknown>
): void => {
    // Callers log before acting (e.g. posting to the app), so tracking must not throw.
    try {
        const amplitudeInstance = getAmplitudeInstance();
        if (!amplitudeInstance) return;

        amplitudeInstance.logEvent(eventName, {
            country: localeCountryCodeMap[locale],
            locale,
            websiteVersion: WEBSITE_VERSION_V2,
            context: uiContext,
            path,
            ...eventProps,
        });
    } catch (e) {
        logger.logException(e, "failed to track user event");
    }
};

export const setUserDataProperties = (
    userData: AuthUserData,
    userProperties: Record<string, unknown> = {}
) => {
    const amplitudeInstance = getAmplitudeInstance();
    if (!amplitudeInstance) return;

    userProperties.isLoggedIn = !!userData;
    if (userProperties.isLoggedIn) {
        // In the app webview the injected UUID (already applied above) stays the user id, so a
        // different web account's id must not be attached to that profile.
        const appConsumerUuid = window.apAppConsumerUuid;
        if (!appConsumerUuid) {
            userData?.uuid && _amplitudeInstance.setUserId(userData.uuid);
        }
        if (!appConsumerUuid || userData?.uuid === appConsumerUuid) {
            userProperties.id = userData?.id;
        }
    }

    setUserProperties(userProperties);
};

export const setUserProperties = (userProperties: Record<string, unknown> = {}) => {
    const amplitudeInstance = getAmplitudeInstance();
    if (!amplitudeInstance) return;

    _amplitudeInstance.setUserProperties(userProperties);
};

export const setBuilderVariationProperty = (
    locale: Locale,
    builderContentPath: string,
    variant: string
) => {
    if (!variant) return;
    const variantProperty = {
        [`${locale}${builderContentPath}`]: variant,
    };

    setUserProperties(variantProperty);
};
